/**
 * Shared Search Overlay — used by BOTH the public Trust Center and the
 * admin "Preview search as a buyer" action. There is exactly one code
 * path, so the two surfaces can never drift.
 *
 * Design contract (from the Phase 3.1 spec):
 *   - Public + admin call the SAME backend service (`SearchService`).
 *   - Every result carries an opaque, public-safe `evidence_ref`.
 *   - Every result deep-links to the exact page/section/framework.
 *   - Progressive disclosure: only the top result per group shows related.
 *   - Analytics events (query/zero_result/click/requested/contact_clicked)
 *     ingested by the same endpoint — no PII sent from the client.
 *
 * Consumers pass:
 *   - `endpoint` : GET URL for search (public or admin preview).
 *   - `analyticsEndpoint` (optional): POST URL for events. Omit in admin
 *     preview so admin previews never pollute buyer analytics.
 *   - `onNavigate(anchor)`: callback that resolves a section_anchor into
 *     a navigation (public uses react-router; admin preview uses a
 *     read-only "open in new tab" behaviour).
 *   - `openRequest`, `openContact` (optional): actions surfaced in the
 *     zero-results experience. Pre-fill the current query.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Search, X, FileText, ShieldCheck, BookOpen, HelpCircle, Building2,
  Zap, Command, ArrowRight, Send, Mail, Lock,
} from 'lucide-react';

const KIND_ICON = {
  resource: FileText,
  policy: BookOpen,
  security_section: ShieldCheck,
  framework: ShieldCheck,
  faq: HelpCircle,
  subprocessor: Building2,
  update: Zap,
};

const ACCESS_META = {
  public: { label: 'Public', cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]' },
  request: { label: 'Request Access', cls: 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]' },
  nda: { label: 'Available under NDA', cls: 'bg-[#F8FAFC] text-[#475569] border-[#CBD5E1]' },
};

// Opaque, client-generated session id (no PII). Regenerated per tab load.
// The backend has a Pydantic `extra: forbid` guard, so no other client
// fingerprinting can be smuggled in.
const SESSION_KEY = 'tc-search-session';
const getSessionId = () => {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = 'tcs-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return 'tcs-anon-fallback';
  }
};

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/* --------------------------- Trigger button --------------------------- */
// Small pill in the header on desktop (⌘K), search icon on mobile.
export const SearchTrigger = ({ onClick, className = '', testid = 'tc-search-trigger' }) => (
  <button type="button" onClick={onClick}
    className={`inline-flex items-center gap-2 h-9 px-3 rounded-full border border-[#E2E8F0] bg-white hover:border-[#CBD5E1] hover:bg-[#F8FAFC] transition-colors text-[#64748B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F62FE]/30 ${className}`}
    data-testid={testid} aria-label="Search Trust Center">
    <Search className="w-4 h-4" />
    <span className="text-xs font-medium hidden sm:inline">Search</span>
    <span className="hidden md:inline-flex items-center gap-0.5 text-[10px] font-mono text-[#94A3B8] border border-[#E2E8F0] rounded px-1 py-0.5 ml-1">
      {isMac() ? <Command className="w-2.5 h-2.5" /> : 'Ctrl'}<span>K</span>
    </span>
  </button>
);

/* --------------------------- Overlay --------------------------- */
export const SearchOverlay = ({
  open, onClose, endpoint, analyticsEndpoint = null,
  onNavigate, openRequest, openContact,
  title = 'Search Trust Center',
  placeholder = 'Search resources, policies, frameworks, FAQs…',
  mode = 'public',
  // Preview parity: when provided, the overlay POSTs to `endpoint` with
  // the caller's body-provider output so unsaved admin changes flow
  // through the SAME backend sanitizer + SearchService as public search.
  bodyProvider = null,
}) => {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ q: '', total: 0, groups: [] });
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  // Track analytics only when we have a live endpoint (public + when
  // provided). Admin preview omits the endpoint so it never inflates
  // buyer analytics.
  const track = useCallback((event, extra = {}) => {
    if (!analyticsEndpoint) return;
    try {
      axios.post(analyticsEndpoint, {
        session_id: getSessionId(),
        event,
        q: (extra.q ?? q ?? '').slice(0, 200),
        kind: extra.kind || null,
        evidence_ref: extra.evidence_ref || null,
        position: typeof extra.position === 'number' ? extra.position : null,
      }).catch(() => { /* fire-and-forget — never block UI on analytics */ });
    } catch { /* ignore */ }
  }, [analyticsEndpoint, q]);

  // Debounce the query so we do not spam the endpoint on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 150);
    return () => clearTimeout(t);
  }, [q]);

  // Run the search whenever the debounced query changes and the overlay is open.
  useEffect(() => {
    if (!open) return undefined;
    if (!debounced) { setData({ q: '', total: 0, groups: [] }); return undefined; }
    let cancelled = false;
    setLoading(true); setError('');
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    // If a bodyProvider is supplied, POST it — same endpoint URL, same
    // response shape. This lets admin preview search the current
    // in-memory (unsaved) draft. Server still runs the shared sanitizer
    // + SearchService, so results are always public-safe.
    const req = bodyProvider
      ? axios.post(endpoint, { q: debounced, limit: 20, ...(bodyProvider() || {}) }, { signal: controller.signal })
      : axios.get(endpoint, { params: { q: debounced, limit: 20 }, signal: controller.signal });
    req
      .then(({ data: r }) => {
        if (cancelled) return;
        setData(r || { q: debounced, total: 0, groups: [] });
        // Fire the analytics event once per debounced query.
        if ((r?.total || 0) === 0) {
          track('zero_result', { q: debounced });
        } else {
          track('query', { q: debounced });
        }
      })
      .catch((err) => {
        if (cancelled || axios.isCancel?.(err) || err?.name === 'CanceledError') return;
        setError('Search is temporarily unavailable.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [debounced, endpoint, open, track, bodyProvider]);

  // Focus + ESC + hotkey handling.
  useEffect(() => {
    if (!open) return undefined;
    setTimeout(() => inputRef.current?.focus(), 10);
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while overlay open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  const handleResultClick = useCallback((result, position) => {
    track('click', { q: debounced, kind: result.kind, evidence_ref: result.evidence_ref, position });
    onNavigate(result.section_anchor);
    onClose();
  }, [debounced, onNavigate, onClose, track]);

  const handleRequest = () => {
    track('requested', { q: debounced });
    if (openRequest) openRequest(debounced);
    onClose();
  };
  const handleContact = () => {
    track('contact_clicked', { q: debounced });
    if (openContact) openContact();
    onClose();
  };

  if (!open) return null;

  const hasQuery = !!debounced;
  const showZero = hasQuery && !loading && (data.total || 0) === 0 && !error;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[10vh] pb-8"
      data-testid="tc-search-overlay">
      <div className="fixed inset-0 bg-[#0A0A0A]/60 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />

      <div role="dialog" aria-modal="true" aria-label={title}
        className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-[#E2E8F0] overflow-hidden max-h-[80vh] flex flex-col">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E8F0]">
          <Search className="w-5 h-5 text-[#94A3B8] shrink-0" />
          <input ref={inputRef} type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="flex-1 outline-none text-[15px] placeholder:text-[#94A3B8] bg-transparent"
            data-testid="tc-search-input" autoComplete="off" spellCheck="false" />
          <button type="button" onClick={onClose} aria-label="Close search"
            className="p-1 rounded hover:bg-[#F1F5F9] text-[#94A3B8]" data-testid="tc-search-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {mode === 'admin' && (
            <div className="px-4 py-2 text-[11px] text-[#B45309] bg-[#FFFBEB] border-b border-[#FDE68A]" data-testid="tc-search-admin-notice">
              Admin preview — this is exactly what buyers see. No analytics recorded.
            </div>
          )}

          {!hasQuery && (
            <div className="px-6 py-10 text-center text-sm text-[#64748B]">
              Start typing to search. Try SOC 2, SSO, penetration test, or DPA.
            </div>
          )}

          {error && (
            <div className="px-6 py-6 text-center text-sm text-[#B91C1C]" data-testid="tc-search-error">{error}</div>
          )}

          {loading && !error && (
            <div className="px-6 py-6 text-center text-sm text-[#94A3B8]" data-testid="tc-search-loading">Searching…</div>
          )}

          {showZero && (
            <ZeroResults query={debounced} onRequest={openRequest ? handleRequest : null} onContact={openContact ? handleContact : null} />
          )}

          {!loading && !error && hasQuery && (data.total || 0) > 0 && (
            <div className="divide-y divide-[#E2E8F0]" data-testid="tc-search-results">
              {(data.groups || []).map((g) => (
                <ResultGroup key={g.kind} group={g} onClick={handleResultClick} onNavigate={onNavigate} onClose={onClose} />
              ))}
            </div>
          )}
        </div>

        {/* Footer keyboard hints (desktop only) */}
        <div className="hidden md:flex items-center justify-between text-[11px] text-[#94A3B8] px-4 py-2 border-t border-[#F1F5F9] bg-[#FBFCFE]">
          <div className="flex items-center gap-3">
            <span><kbd className="font-mono border border-[#E2E8F0] rounded px-1 py-0.5 text-[10px]">↵</kbd> to open</span>
            <span><kbd className="font-mono border border-[#E2E8F0] rounded px-1 py-0.5 text-[10px]">Esc</kbd> to close</span>
          </div>
          {data.total > 0 && <span>{data.total} result{data.total !== 1 ? 's' : ''}</span>}
        </div>
      </div>
    </div>
  );
};

/* --------------------------- Result group --------------------------- */
const ResultGroup = ({ group, onClick, onNavigate, onClose }) => {
  const Icon = KIND_ICON[group.kind] || FileText;
  return (
    <div className="py-2" data-testid={`tc-search-group-${group.kind}`}>
      <div className="flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-wider text-[#94A3B8] font-semibold">
        <Icon className="w-3.5 h-3.5" />
        {group.label}
      </div>
      {group.results.map((r, i) => (
        <ResultCard key={r.evidence_ref} result={r} position={i} isTop={i === 0} onClick={onClick} onNavigate={onNavigate} onClose={onClose} />
      ))}
    </div>
  );
};

/* --------------------------- Single result --------------------------- */
const ResultCard = ({ result, position, isTop, onClick, onNavigate, onClose }) => {
  const meta = ACCESS_META[result.access] || ACCESS_META.public;
  return (
    <div className="px-4 py-2" data-testid={`tc-search-result-item-${result.evidence_ref}`}>
      <button type="button" onClick={() => onClick(result, position)}
        className="w-full text-left group flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F8FAFC] focus:outline-none focus-visible:bg-[#F1F5F9]"
        data-testid={`tc-search-result-btn-${result.evidence_ref}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-[14px] font-semibold text-[#0A0A0A] truncate">{result.title}</h4>
            {result.access !== 'public' && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${meta.cls} shrink-0`}>{meta.label}</span>
            )}
          </div>
          {result.snippet && (
            <p className="text-xs text-[#64748B] mt-0.5 line-clamp-2">{result.snippet}</p>
          )}
          {result.tags && result.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {result.tags.slice(0, 4).map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569]">{t}</span>
              ))}
            </div>
          )}
        </div>
        <ArrowRight className="w-4 h-4 text-[#CBD5E1] group-hover:text-[#0F62FE] shrink-0 mt-1" />
      </button>

      {/* Progressive disclosure: related content on the top result only. */}
      {isTop && result.related && result.related.length > 0 && (
        <div className="ml-8 mt-1 mb-2 border-l-2 border-[#E2E8F0] pl-3" data-testid={`tc-search-related-${result.evidence_ref}`}>
          <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold mb-1">Related</p>
          <div className="flex flex-wrap gap-1">
            {result.related.map((rel) => {
              const RelIcon = KIND_ICON[rel.kind] || FileText;
              return (
                <button key={rel.evidence_ref} type="button"
                  onClick={(e) => { e.stopPropagation(); onNavigate(rel.section_anchor); onClose(); }}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-[#E2E8F0] bg-white hover:border-[#0F62FE] hover:text-[#0F62FE] text-[#475569] transition-colors"
                  data-testid={`tc-search-related-item-${rel.evidence_ref}`}>
                  <RelIcon className="w-3 h-3" /> {rel.title}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* --------------------------- Zero results --------------------------- */
const ZeroResults = ({ query, onRequest, onContact }) => (
  <div className="px-6 py-10 text-center" data-testid="tc-search-zero">
    <Search className="w-8 h-8 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.5} />
    <h4 className="font-semibold text-[15px] text-[#0A0A0A]">
      We couldn&rsquo;t find anything about &ldquo;{query}&rdquo;.
    </h4>
    <p className="text-sm text-[#64748B] mt-1.5 mb-4 max-w-md mx-auto">
      This might be documented privately, or something we haven&rsquo;t published yet. Choose how you&rsquo;d like to follow up:
    </p>
    <div className="flex flex-wrap justify-center gap-2">
      {onRequest && (
        <button type="button" onClick={onRequest}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E2E8F0] bg-white hover:border-[#0F62FE] hover:text-[#0F62FE] text-sm font-medium text-[#0A0A0A] transition-colors"
          data-testid="tc-search-zero-request">
          <Lock className="w-3.5 h-3.5" /> Request Documentation
        </button>
      )}
      {onContact && (
        <button type="button" onClick={onContact}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E2E8F0] bg-white hover:border-[#0F62FE] hover:text-[#0F62FE] text-sm font-medium text-[#0A0A0A] transition-colors"
          data-testid="tc-search-zero-contact">
          <Mail className="w-3.5 h-3.5" /> Contact Security
        </button>
      )}
      {onRequest && (
        <button type="button" onClick={onRequest}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E2E8F0] bg-white hover:border-[#0F62FE] hover:text-[#0F62FE] text-sm font-medium text-[#0A0A0A] transition-colors"
          data-testid="tc-search-zero-ask">
          <Send className="w-3.5 h-3.5" /> Ask a Question
        </button>
      )}
    </div>
    <p className="text-xs text-[#94A3B8] mt-4">Your search will be pre-filled so you don&rsquo;t have to retype it.</p>
  </div>
);

export default SearchOverlay;
